import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { observer } from "mobx-react";
import styled from "styled-components";
import { API_HOST, RequestState } from "../../modules/constants";
import { colors } from "../../theme";
import { onboardingController } from "../../modules/controllers";
import { userStore } from "../../modules/stores";
import { getSessionCookie } from "../../modules/utils";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    ${colors.primary} 0%,
    #8b0000 50%,
    #660000 100%
  );
  padding: 32px 16px 48px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const ContentCard = styled.div`
  width: 100%;
  max-width: 1100px;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  background: #ffffff;

  @media (max-width: 900px) {
    padding: 24px;
  }
`;

const Section = styled.div`
  margin-top: 28px;
  padding: 20px;
  border: 1px solid #f0f0f0;
  border-radius: 16px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  margin-top: 8px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  margin-top: 16px;
`;

const SectionHeader = styled(Typography)`
  font-weight: 700;
  margin-bottom: 20px;
  color: #2c3e50;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const PrimaryButton = styled(Button)`
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  background: ${colors.gradients.primary} !important;
  box-shadow: 0 10px 30px rgba(166, 2, 22, 0.3);
`;

const SecondaryButton = styled(Button)`
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  border: 2px solid ${colors.primary};
  color: ${colors.primary};
`;

export const SettingsForm = observer(({ controller }) => {
  const {
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    getUpdateSettingsMessage,
    getUpdateSettingsRequestState,
  } = controller;

  const [mychartStatus, setMychartStatus] = useState({
    loading: true,
    linked: false,
    status: "not-linked",
    lastSync: null,
  });
  const [mychartError, setMychartError] = useState("");
  const [preferenceOptions, setPreferenceOptions] = useState({
    favoriteFoods: [],
    allergies: [],
  });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");

  const epicIss = process.env.REACT_APP_EPIC_ISS || "";

  const heightOptions = useMemo(
    () => ({
      feet: Array.from({ length: 6 }, (_, idx) => String(idx + 3)),
      inches: Array.from({ length: 12 }, (_, idx) => String(idx)),
    }),
    []
  );

  const normalizeList = (value) => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "string") return [];
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const togglePreference = (key, value) => {
    const current = normalizeList(settings[key]);
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    updateSetting(key, next);
  };

  const parseHeight = (value) => {
    if (!value || typeof value !== "string") {
      return { feet: "", inches: "" };
    }
    const cmMatch = value.match(/([\d.]+)\s*cm/i);
    if (cmMatch) {
      const cm = Number(cmMatch[1]);
      if (!Number.isFinite(cm) || cm <= 0) {
        return { feet: "", inches: "" };
      }
      const totalInches = Math.round(cm / 2.54);
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      return { feet: String(feet), inches: String(inches) };
    }
    const match = value.match(/(\d+)\s*'\s*(\d+)?/);
    if (!match) return { feet: "", inches: "" };
    return {
      feet: match[1] || "",
      inches: match[2] || "0",
    };
  };

  const updateHeight = (nextFeet, nextInches) => {
    const feetValue = nextFeet ?? heightFeet;
    const inchesValue = nextInches ?? heightInches;
    setHeightFeet(feetValue);
    setHeightInches(inchesValue);
    if (!feetValue && !inchesValue) {
      updateSetting("height", "");
      return;
    }
    const normalizedInches = inchesValue || "0";
    updateSetting("height", `${feetValue}'${normalizedInches}"`);
  };

  const loadPreferenceOptions = async () => {
    setOptionsLoading(true);
    setOptionsError("");
    try {
      const cookie = await getSessionCookie();
      if (!cookie) {
        setOptionsLoading(false);
        return;
      }
      const response = await axios.get(`${API_HOST}user/onboarding/options`, {
        headers: { Authorization: cookie },
      });
      setPreferenceOptions({
        favoriteFoods: response?.data?.favoriteFoods || [],
        allergies: response?.data?.allergies || [],
      });
    } catch (err) {
      setOptionsError(
        err?.response?.data?.error ||
          "Could not load preference options right now."
      );
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadMyChartStatus = async () => {
    setMychartError("");
    const cookie = await getSessionCookie();
    if (!cookie) {
      setMychartStatus((prev) => ({ ...prev, loading: false }));
      return;
    }
    try {
      const response = await axios.get(`${API_HOST}api/mychart/status`, {
        headers: { Authorization: cookie },
      });
      setMychartStatus({ loading: false, ...response.data });
    } catch (err) {
      setMychartStatus((prev) => ({ ...prev, loading: false }));
      setMychartError("Could not load MyChart status.");
    }
  };

  const startMyChartConnect = () => {
    if (!epicIss) {
      setMychartError("Epic sandbox issuer is not configured.");
      return;
    }
    window.location.href = `${API_HOST}api/auth/epic/start?iss=${encodeURIComponent(
      epicIss
    )}`;
  };

  const importLabs = async () => {
    setMychartError("");
    const cookie = await getSessionCookie();
    if (!cookie) return;
    setMychartStatus((prev) => ({ ...prev, loading: true }));
    try {
      await axios.post(
        `${API_HOST}api/mychart/labs/import`,
        {},
        { headers: { Authorization: cookie } }
      );
      await loadMyChartStatus();
    } catch (err) {
      setMychartError(
        err?.response?.data?.error || "Could not import labs from MyChart."
      );
      setMychartStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const unlinkMyChart = async () => {
    setMychartError("");
    const cookie = await getSessionCookie();
    if (!cookie) return;
    setMychartStatus((prev) => ({ ...prev, loading: true }));
    try {
      await axios.post(
        `${API_HOST}api/mychart/unlink`,
        {},
        { headers: { Authorization: cookie } }
      );
      await loadMyChartStatus();
    } catch (err) {
      setMychartError("Could not unlink MyChart right now.");
      setMychartStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings();
  };

  const isLoading = getUpdateSettingsRequestState === RequestState.REQUESTED;
  const shouldShowOnboardingRelaunch =
    userStore.userObj && !userStore.hasCompletedOnboarding;

  useEffect(() => {
    controller.loadUserSettings();
    loadMyChartStatus();
    loadPreferenceOptions();
  }, [controller]);

  useEffect(() => {
    const parsed = parseHeight(settings.height);
    setHeightFeet(parsed.feet);
    setHeightInches(parsed.inches);
  }, [settings.height]);

  return (
    <PageWrapper>
      <ContentCard>
        <HeaderRow>
          <div>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 800, color: colors.primary, mb: 1 }}
            >
              Settings
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Update your account details and health profile to keep your meal
              plans accurate.
            </Typography>
          </div>
          {shouldShowOnboardingRelaunch && (
            <Button
              variant="outlined"
              onClick={() => onboardingController.openFromSettings()}
            >
              Relaunch onboarding
            </Button>
          )}
        </HeaderRow>

        {getUpdateSettingsMessage && (
          <Alert
            severity={
              getUpdateSettingsMessage.includes("successfully")
                ? "success"
                : "error"
            }
            sx={{ mt: 3, borderRadius: 2 }}
          >
            {getUpdateSettingsMessage}
          </Alert>
        )}

        <form
          style={{ display: "flex", flexDirection: "column" }}
          onSubmit={handleSubmit}
        >
          <Columns>
            <Section>
              <SectionHeader variant="h6">Account Information</SectionHeader>
              <FieldStack>
                <TextField
                  label="First Name"
                  value={settings.firstName}
                  onChange={(e) => updateSetting("firstName", e.target.value)}
                  required
                  fullWidth
                  disabled={isLoading}
                />
                <TextField
                  label="Last Name"
                  value={settings.lastName}
                  onChange={(e) => updateSetting("lastName", e.target.value)}
                  required
                  fullWidth
                  disabled={isLoading}
                />
                <TextField
                  label="Email"
                  value={settings.email}
                  onChange={(e) => updateSetting("email", e.target.value)}
                  required
                  type="email"
                  fullWidth
                  disabled={isLoading}
                />
                <TextField
                  label="Country"
                  value={settings.country}
                  onChange={(e) => updateSetting("country", e.target.value)}
                  fullWidth
                  disabled={isLoading}
                />
                <FormControl fullWidth disabled={isLoading}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={settings.gender}
                    label="Gender"
                    onChange={(e) => updateSetting("gender", e.target.value)}
                  >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="prefer-not-to-say">
                      Prefer not to say
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Date of Birth"
                  value={settings.dateOfBirth}
                  onChange={(e) => updateSetting("dateOfBirth", e.target.value)}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled={isLoading}
                />
              </FieldStack>
            </Section>

            <Section>
              <SectionHeader variant="h6">Health Profile</SectionHeader>
              <FieldStack>
                <TextField
                  label="Weight (lbs)"
                  value={settings.weight}
                  onChange={(e) => updateSetting("weight", e.target.value)}
                  fullWidth
                  disabled={isLoading}
                  type="number"
                  inputProps={{ min: 0 }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Height
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth disabled={isLoading}>
                        <InputLabel>Feet</InputLabel>
                        <Select
                          value={heightFeet}
                          label="Feet"
                          onChange={(e) => updateHeight(e.target.value, null)}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {heightOptions.feet.map((foot) => (
                            <MenuItem key={foot} value={foot}>
                              {foot}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth disabled={isLoading}>
                        <InputLabel>Inches</InputLabel>
                        <Select
                          value={heightInches}
                          label="Inches"
                          onChange={(e) => updateHeight(null, e.target.value)}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {heightOptions.inches.map((inch) => (
                            <MenuItem key={inch} value={inch}>
                              {inch}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Allergies
                  </Typography>
                  {optionsError && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {optionsError}
                    </Alert>
                  )}
                  {optionsLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading options...
                    </Typography>
                  ) : preferenceOptions.allergies.length ? (
                    <Grid container spacing={1}>
                      {preferenceOptions.allergies.map((allergy) => (
                        <Grid item xs={12} sm={6} key={allergy}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={normalizeList(
                                  settings.allergies
                                ).includes(allergy)}
                                onChange={() =>
                                  togglePreference("allergies", allergy)
                                }
                              />
                            }
                            label={allergy}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No allergy options available yet.
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Favorite foods
                  </Typography>
                  {optionsLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading options...
                    </Typography>
                  ) : preferenceOptions.favoriteFoods.length ? (
                    <Grid container spacing={1}>
                      {preferenceOptions.favoriteFoods.map((food) => (
                        <Grid item xs={12} sm={6} key={food}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={normalizeList(
                                  settings.favoriteFoods
                                ).includes(food)}
                                onChange={() =>
                                  togglePreference("favoriteFoods", food)
                                }
                              />
                            }
                            label={food}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No favorite food options available yet.
                    </Typography>
                  )}
                </Box>
              </FieldStack>
            </Section>

            <Section>
            <SectionHeader variant="h6">Security</SectionHeader>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your current password to set a new one.
              </Typography>
              <FieldStack>
                <TextField
                  label="Current Password"
                  type="password"
                  value={settings.currentPassword}
                  onChange={(e) =>
                    updateSetting("currentPassword", e.target.value)
                  }
                  fullWidth
                  disabled={isLoading}
                />
                <TextField
                  label="New Password"
                  type="password"
                  value={settings.newPassword}
                  onChange={(e) => updateSetting("newPassword", e.target.value)}
                  fullWidth
                  disabled={isLoading}
                />
              </FieldStack>
            </Section>

            <Section>
              <SectionHeader variant="h6">
                MyChart (Epic sandbox) Labs
              </SectionHeader>
              {mychartError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {mychartError}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Connect your Epic sandbox MyChart to import lab results during
                onboarding. Tokens stay server-side; we only expose status here.
              </Typography>
              <FieldStack>
                <Typography variant="body1">
                  Status:{" "}
                  {mychartStatus.loading
                    ? "Checking..."
                    : mychartStatus.linked
                    ? "Connected"
                    : "Not connected"}
                </Typography>
                {mychartStatus.lastSync && (
                  <Typography variant="body2" color="text.secondary">
                    Last sync: {new Date(mychartStatus.lastSync).toLocaleString()}
                  </Typography>
                )}
                <ActionsRow style={{ justifyContent: "flex-start" }}>
                  <PrimaryButton
                    type="button"
                    variant="contained"
                    onClick={startMyChartConnect}
                    disabled={mychartStatus.loading}
                  >
                    {mychartStatus.linked ? "Reconnect MyChart" : "Connect MyChart"}
                  </PrimaryButton>
                  <Button
                    variant="outlined"
                    onClick={importLabs}
                    disabled={mychartStatus.loading || !mychartStatus.linked}
                  >
                    Import labs
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    onClick={unlinkMyChart}
                    disabled={mychartStatus.loading || !mychartStatus.linked}
                  >
                    Unlink
                  </Button>
                </ActionsRow>
              </FieldStack>
            </Section>
          </Columns>

          <Divider sx={{ my: 3 }} />

          <ActionsRow>
            <SecondaryButton onClick={resetSettings} disabled={isLoading}>
              Reset
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </PrimaryButton>
          </ActionsRow>
        </form>
      </ContentCard>
    </PageWrapper>
  );
});

export default SettingsForm;
